import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { IBreachDetails } from "../../../models/breach-details.interface";
import ApplePayButton from "../../ApplePayButton/ApplePayButton";
import { useApplePay } from "../../ApplePayButton/contexts/ApplePay/ApplePayContext";
import { CreditCardButton } from "../../CreditCardButton/CreditCardButton";
import { GooglePayButton } from "../../GooglePayButton/GooglePayButton";
import { PaymentMethod } from "../types";

interface IProps {
  breachDetails: IBreachDetails;
  email: string;
  paymentMethod: string;
}
export const Payment = ({ breachDetails, email, paymentMethod }: IProps) => {
  const { isApplePayAvailable, handleApplePayClick } = useApplePay();
  const { ticketNbr, vrm, amount, details, location } = breachDetails;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  return (
    <>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Payment Summary
        </Typography>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>Breach ID:</Typography>
          <Typography fontWeight={500}>{ticketNbr}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>Vehicle Registration:</Typography>
          <Typography fontWeight={500}>{vrm}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>Breach Details:</Typography>
          <Typography fontWeight={500}>{details}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>Car Park Location:</Typography>
          <Typography fontWeight={500}>{location}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>Payment Method:</Typography>
          <Typography fontWeight={500}>{paymentMethod}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontWeight={600}>Email:</Typography>
          <Typography fontWeight={500}>{email}</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Total Amount:
          </Typography>
          <Typography variant="h6" fontWeight={700} color="#fe6611">
            NZD$ {Number(amount).toFixed(2)}
          </Typography>
        </Box>
      </Paper>
      <Box
        display="inline-block"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        sx={{ width: "100%" }}
      >
        {isApplePayAvailable && paymentMethod === PaymentMethod.Apple && (
          <ApplePayButton
            type="pay"
            disabled={false}
            style={{ height: "50px", width: "100%" }}
            onClick={handleApplePayClick(
              Number(amount),
              breachDetails,
              email,
              setError
            )}
          />
        )}
        {paymentMethod === PaymentMethod.Card && (
          <CreditCardButton details={breachDetails} email={email} />
        )}
        {paymentMethod === PaymentMethod.Google && (
          <GooglePayButton
            amount={Number(amount)}
            breachDetails={breachDetails}
            locale="en"
            email={email}
            disabled={loading}
            setGooglePayLoading={setLoading}
          />
        )}
        {loading && (
          <Typography variant="body1" color="textSecondary" mt={2} mb={2}>
            <CircularProgress size={20} sx={{ mr: 2, mt: 1 }} />
            Please wait while we process your payment...
          </Typography>
        )}
        {error && (
          <Typography variant="body1" color="error" mt={2} mb={2}>
            {error}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        align="center"
      >
        Your payment information is secure and encrypted
        <br />
        You will receive a confirmation email after successful payment
      </Typography>
    </>
  );
};
